import { db } from "@/lib/firebase";
import {
  collection,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  addDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { Firestore } from "firebase/firestore";

export class ChatModerationService {
  private static instance: ChatModerationService;

  static getInstance(): ChatModerationService {
    if (!ChatModerationService.instance) {
      ChatModerationService.instance = new ChatModerationService();
    }
    return ChatModerationService.instance;
  }

  // League moderation features
  async moderateMember(
    leagueId: string,
    adminId: string,
    memberId: string,
    action: "mute" | "unmute" | "ban" | "unban" | "promote" | "demote"
  ): Promise<void> {
    if (!db) throw new Error("Firestore not initialized");
    const dbInstance = db as Firestore;

    try {
      // Get the league document
      const leagueRef = doc(dbInstance, "leagues", leagueId);
      const leagueDoc = await getDoc(leagueRef);

      if (!leagueDoc.exists()) {
        throw new Error("League not found");
      }

      const leagueData = leagueDoc.data();
      const members = leagueData.members || [];

      // Find the target member
      const memberIndex = members.findIndex(
        (m: any) =>
          m.golferId === memberId || m.userId === memberId || m.id === memberId
      );

      if (memberIndex === -1) {
        throw new Error("Member not found in league");
      }

      // Apply the moderation action
      const updatedMembers = [...members];
      const member = updatedMembers[memberIndex];

      switch (action) {
        case "mute":
          member.isMuted = true;
          member.mutedAt = new Date();
          member.mutedBy = adminId;
          break;
        case "unmute":
          member.isMuted = false;
          delete member.mutedAt;
          delete member.mutedBy;
          break;
        case "ban":
          member.isBanned = true;
          member.bannedAt = new Date();
          member.bannedBy = adminId;
          break;
        case "unban":
          member.isBanned = false;
          delete member.bannedAt;
          delete member.bannedBy;
          break;
        case "promote":
          member.role = member.role === "member" ? "captain" : "admin";
          break;
        case "demote":
          member.role = member.role === "admin" ? "captain" : "member";
          break;
      }

      // Update the league document
      await updateDoc(leagueRef, {
        members: updatedMembers,
      });

      // Log the moderation action
      await addDoc(collection(dbInstance, "moderationLogs"), {
        leagueId,
        adminId,
        memberId,
        action,
        timestamp: serverTimestamp(),
      });

      console.log(
        `[Decoupled] Admin ${adminId} performed ${action} on member ${memberId} in league ${leagueId}`
      );
    } catch (error) {
      console.error("Moderation action failed:", error);
      throw error;
    }
  }

  async deleteMessage(messageId: string, adminId: string): Promise<void> {
    if (!db) throw new Error("Firestore not initialized");
    const dbInstance = db as Firestore;

    try {
      const messageRef = doc(dbInstance, "messages", messageId);
      await updateDoc(messageRef, {
        isDeleted: true,
        deletedBy: adminId,
        deletedAt: serverTimestamp(),
      });
      console.log(`[Decoupled] Message ${messageId} deleted by admin ${adminId}`);
    } catch (error) {
      console.error("Failed to delete message:", error);
      throw error;
    }
  }

  async pinMessage(messageId: string, adminId: string): Promise<void> {
    if (!db) throw new Error("Firestore not initialized");
    const dbInstance = db as Firestore;

    try {
      const messageRef = doc(dbInstance, "messages", messageId);
      await updateDoc(messageRef, {
        isPinned: true,
        pinnedBy: adminId,
        pinnedAt: serverTimestamp(),
      });
      console.log(`[Decoupled] Message ${messageId} pinned by admin ${adminId}`);
    } catch (error) {
      console.error("Failed to pin message:", error);
      throw error;
    }
  }

  async getLeagueMembers(leagueId: string): Promise<any[]> {
    if (!db) throw new Error("Firestore not initialized");
    const dbInstance = db as Firestore;

    try {
      const leagueDoc = await getDoc(doc(dbInstance, "leagues", leagueId));
      if (!leagueDoc.exists()) {
        console.warn(`League with id ${leagueId} not found.`);
        return [];
      }

      const leagueData = leagueDoc.data();
      const memberObjects = leagueData.members || [];
      const memberIds = memberObjects
        .map((m: any) => m.golferId || m.id || m.userId)
        .filter(Boolean);

      if (memberIds.length === 0) {
        return [];
      }

      // Chunk memberIds into arrays of 30 for Firestore 'in' query limitation
      const chunks = [];
      for (let i = 0; i < memberIds.length; i += 30) {
        chunks.push(memberIds.slice(i, i + 30));
      }

      const members: any[] = [];
      const usersCollection = collection(dbInstance, "users");

      for (const chunk of chunks) {
        const usersQuery = query(usersCollection, where("id", "in", chunk));
        const usersSnapshot = await getDocs(usersQuery);
        usersSnapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          const memberObj = memberObjects.find(
            (m: any) => (m.golferId || m.id || m.userId) === userDoc.id
          );

          members.push({
            id: userDoc.id,
            name: userData.displayName || userData.name || "Unknown Member",
            profileImage: userData.profileImage || userData.avatar,
            role: memberObj?.role || "member",
            status: "active",
            joinedAt: memberObj?.joinedAt?.toDate
              ? memberObj.joinedAt.toDate()
              : new Date(memberObj?.joinedAt || Date.now()),
            isOnline: false, // Placeholder for presence
          });
        });
      }

      return members;
    } catch (error) {
      console.error(
        `Failed to get league members for league ${leagueId}:`,
        error
      );
      return [];
    }
  }
}
export const chatModerationService = ChatModerationService.getInstance();
